import { Injectable, NgZone, inject } from '@angular/core';
import { RestApiService } from '@core/services/rest-api.service';
import { environment } from '@env/environment';
import { SplitterWithGridHelperService } from '@features/vt-grid/services/splitter-with-grid-helper.service';
import { Entity } from '@shared/constants/entity';
import { TranslateService } from '@ngx-translate/core';
import { CreateGeofenceDialogComponent } from '@pages/fleet/components/create-geofence-dialog/create-geofence-dialog.component';
import { MapShape } from '@features/map/interfaces';
import { BehaviorSubject, Subject, take, tap } from 'rxjs';
import { DeleteFleetLocationDialogComponent } from '../components/delete-fleet-location-dialog/delete-fleet-location-dialog.component';
import {
  AssetGeofenceLocationUpsertPayload,
  FleetLocationForm,
  PartialFleetLocationData,
  VehicleGeofenceLocationUpsertPayload,
} from '../interfaces';
import { SnackBarConfig } from '@shared/constants/snackbar-config';
import { DialogService } from '@core/services/dialog.service';
import { MapShapeType } from '@features/map/constants/constants';
import { DvSnackbarService } from '@devinco/ui/dds/snackbar';

@Injectable({ providedIn: 'root' })
export class FleetGeofenceService {
  private dialog = inject(DialogService);
  private api = inject(RestApiService);
  private translate = inject(TranslateService);
  private snackBar = inject(DvSnackbarService);
  private gridEntityHelperService = inject(SplitterWithGridHelperService);
  private zone = inject(NgZone);

  geofences$ = new BehaviorSubject<MapShape[] | undefined>([]);
  deleteGeofenceTrigger$ = new Subject<number | string>();

  openTempGeofenceCreationPopup(partialData: PartialFleetLocationData): void {
    this.dialog
      .open<undefined, CreateGeofenceDialogComponent>({
        title: this.translate.instant(
          'Fleet.TemporaryGeofence.CreateForFleetItem',
          { fleetDescription: partialData.PopupDescription }
        ),
        confirmText: 'General.Save',
        cancelText: 'General.Cancel',
        contentComponent: CreateGeofenceDialogComponent,
        hideFooter: true,
      })
      .closed.pipe(take(1))
      .subscribe(({ responseData, source }) => {
        if (source === 'confirm') {
          this.upsertFleetLocation(responseData, partialData);
        }
      });
  }

  deleteFleetLocation(fleetLocationId: number, entityId?: string): void {
    this.api.delete(Entity.FleetLocation, fleetLocationId).subscribe(() => {
      const geofences = this.geofences$.getValue();
      const deletableGeofenceIndex = geofences?.findIndex(
        g => g.id === fleetLocationId
      );
      this.deleteGeofenceTrigger$.next(fleetLocationId);

      if (deletableGeofenceIndex) {
        geofences?.splice(deletableGeofenceIndex, 1);
        this.geofences$.next(geofences);
      }
      if (entityId) {
        this.gridEntityHelperService.refreshRowData(entityId);
      }
    });
  }

  openDeleteFleetLocationDialog(deletableFleetLocationIds: number[]): void {
    this.zone.run(() => {
      const dialogPayload: MapShape[] | undefined = this.geofences$
        .getValue()
        ?.filter(geofence =>
          deletableFleetLocationIds.some(id => id === geofence.id)
        )
        .map(
          geofence =>
            ({
              id: geofence.id,
              description: geofence.description,
              entityId: geofence.entityId,
            }) as MapShape
        );

      if (!dialogPayload?.length) return;

      this.dialog
        .open<MapShape[], DeleteFleetLocationDialogComponent>({
          size: 'md',
          type: 'error',
          title: 'DeleteGeofenceDialog.Title',
          confirmText: 'General.Confirm',
          cancelText: 'General.Cancel',
          contentComponent: DeleteFleetLocationDialogComponent,
          data: dialogPayload,
          hideFooter: true,
        })
        .closed.pipe(take(1))
        .subscribe(({ responseData, source }) => {
          if (responseData && source === 'confirm') {
            this.deleteFleetLocation(
              responseData,
              this.geofences$.getValue()?.find(geo => geo.id === responseData)
                ?.entityId as string
            );
          }
        });
    });
  }

  private upsertFleetLocation(
    fleetLocationForm: FleetLocationForm,
    partialData: PartialFleetLocationData
  ): void {
    const payload:
      | AssetGeofenceLocationUpsertPayload
      | VehicleGeofenceLocationUpsertPayload = {
      Description: fleetLocationForm.Description,
      ...('AssetID' in partialData
        ? { AssetID: partialData.AssetID }
        : {
            TrackerObjectID: (
              partialData as VehicleGeofenceLocationUpsertPayload
            ).TrackerObjectID,
          }),
      GeoJson: JSON.stringify({
        type: 'circle',
        center: {
          lat: partialData.Coordinates?.lat,
          lng: partialData.Coordinates?.lng,
        },
        radius: fleetLocationForm.Radius,
      }),
      NotifyOut: true,
      IsTemporary: true,
    };
    this.api
      .upsert(Entity.FleetLocation, '0', payload)
      .pipe(
        tap(newShapeId => {
          if (!partialData.Coordinates?.lat || !partialData.Coordinates.lng)
            return;

          if (partialData.EntityId) {
            this.gridEntityHelperService.refreshRowData(partialData.EntityId);
          }

          this.showSuccessNotification();
          this.updateGeofencesInMap(fleetLocationForm, partialData, newShapeId);
        })
      )
      .subscribe();
  }

  private showSuccessNotification(): void {
    this.snackBar.open(
      this.translate.instant('Fleet.TemporaryGeofence.UpsertSuccessful'),
      SnackBarConfig.Success
    );
  }

  private updateGeofencesInMap(
    tempGeofenceForm: FleetLocationForm,
    partialData: PartialFleetLocationData,
    newShapeId?: number
  ): void {
    // vehicle's position at the moment the geofence was created, coinciding with its center
    const linkedVehicleId =
      'TrackerObjectID' in partialData
        ? partialData.TrackerObjectID
        : undefined;

    const newShape: MapShape = {
      type: MapShapeType.Circle,
      radius: tempGeofenceForm.Radius,
      center: {
        lat: partialData.Coordinates!.lat,
        lng: partialData.Coordinates!.lng,
      },
      color: 'blue',
      id: newShapeId,
      description: tempGeofenceForm.Description,
      entityId: partialData.EntityId,
      linkedVehicleId,
      linkedPosition:
        linkedVehicleId !== undefined ? partialData.Coordinates : undefined,
      skipFitBounds: true,
    };
    const existingShapes = this.geofences$.getValue();
    if (existingShapes?.length) {
      this.geofences$.next([...existingShapes, newShape]);
    } else {
      this.geofences$.next([newShape]);
      setTimeout(() => {
        this.geofences$.next([]);
      }, environment.snackBarDisplayTime);
    }
  }
}
